import Api from '../../shared/Api';
import {
    ExternalAnnotation,
    DeliveryAnnotation,
    ExternalDeliveryAnnotation
} from './AnnotationTypes';

class PlanAnnotationsApi {
    static editExternalFileAnnotation(fileId: number): Promise<any> {
        return new Api().get(`/views/planannotation/external/${fileId}/edit`);
    }

    static editDeliveryFileAnnotation(fileId: number): Promise<any> {
        return new Api().get(`/views/planannotation/delivery/${fileId}/edit`);
    }

    static editAnnotation(planOrderStoreyFileId: number, planAnnotationType: string): Promise<any> {
        return new Api().get(
            `/views/planannotation/${planOrderStoreyFileId}/edit/${planAnnotationType}`
        );
    }

    static saveAnnotation(
        fileId: number,
        data: any,
        hasDoneCrop: boolean,
        revisionType: number,
        annotationType: string,
        hasDoneFlip?: boolean
    ): Promise<any> {
        if (annotationType === ExternalAnnotation) {
            if (hasDoneCrop) {
                let dataToPost = data.originalUpload ? data.originalUpload : data.upload;
                return new Api()
                    .postFiles(`/views/planannotation/external/${fileId}/create`, dataToPost)
                    .then((res) => {
                        if (data.canvasContent) {
                            return this.saveExternalFileRevision(res.id, data);
                        }

                        return {
                            annotationType: annotationType,
                            id: res.id,
                            canvasContent: data.canvasContent,
                            fileReference: res.fileReference,
                            thumbnailReference: res.thumbnailReference,
                            fileType: res.fileType,
                            filename: res.filename
                        };
                    });
            } else {
                return this.saveExternalFileRevision(fileId, data);
            }
        } else if (annotationType === ExternalDeliveryAnnotation) {
            return this.saveExternalDeliveryFileRevision(fileId, data);
        } else if (annotationType === DeliveryAnnotation) {
            return this.saveDeliveryRevision(fileId, data);
        } else if (hasDoneCrop) {
            let dataToPost = data.originalUpload ? data.originalUpload : data.upload;
            return new Api()
                .postFiles(`/views/planannotation/${fileId}/create`, dataToPost)
                .then((res) => {
                    if (data.canvasContent) {
                        return this.saveAnnotationRevision(fileId, data, revisionType, res);
                    }

                    return {
                        annotationType: annotationType,
                        id: fileId,
                        canvasContent: data.canvasContent,
                        fileReference: res.fileReference,
                        thumbnailReference: res.thumbnailReference,
                        original: res,
                        isAnnotation: res.isAnnotation
                    };
                });
        } else {
            return this.saveAnnotationRevision(fileId, data, revisionType, undefined, hasDoneFlip);
        }
    }

    static saveDeliveryRevision(fileId: number, data: any): Promise<any> {
        return new Api()
            .put(`/views/planannotation/delivery/${fileId}/update`, {
                canvasContent: data.canvasContent
            })
            .then((id) => {
                return new Api()
                    .postFiles(`/views/planannotation/delivery/${id}/update/files`, data.upload)
                    .then((res) => {
                        return {
                            annotationType: DeliveryAnnotation,
                            id: res.id,
                            canvasContent: data.canvasContent,
                            fileReference: res.fileReference,
                            thumbnailReference: res.thumbnailReference,
                            parentId: fileId,
                            fileType: res.fileType,
                            filename: res.filename
                        };
                    });
            });
    }

    static saveAnnotationTexts(planOrderStoreyFileId: number, data: any): Promise<any> {
        return new Api().put(`/views/planannotation/${planOrderStoreyFileId}/update/texts`, {
            canvasContent: data.canvasContent,
            fontSize: data.fontSize
        });
    }

    static saveAnnotationRevision(
        planOrderStoreyFileId: number,
        data: any,
        revisionType: number,
        original?: any,
        hasDoneFlip?: boolean
    ): Promise<any> {
        let url = hasDoneFlip
            ? `/views/planannotation/${original ? original.id : planOrderStoreyFileId}/update/agent`
            : `/views/planannotation/${original ? original.id : planOrderStoreyFileId}/update`;
        return new Api()
            .put(url, {
                canvasContent: data.canvasContent,
                isAdvanced: data.isAdvanced,
                revisionType: revisionType,
                fontSize: data.fontSize,
                hasDoneFlip: hasDoneFlip
            })
            .then((result) => {
                if (data.upload) {
                    return new Api()
                        .postFiles(
                            `/views/planannotation/${result.planOrderStoreyFileRevisionId}/update/files`,
                            data.upload
                        )
                        .then((res) => {
                            return {
                                id: planOrderStoreyFileId,
                                canvasContent: data.canvasContent,
                                fileReference: res.reference,
                                thumbnailReference: res.thumbnailReference,
                                original: original,
                                isAnnotation: res.isAnnotation,
                                planOrderStoreyFileId: planOrderStoreyFileId
                            };
                        });
                }

                return null;
            });
    }

    static saveExternalFileRevision(fileId: number, data: any): Promise<any> {
        return new Api()
            .put(`/views/planannotation/external/${fileId}/update`, {
                canvasContent: data.canvasContent
            })
            .then((result) => {
                return new Api()
                    .postFiles(
                        `/views/planannotation/external/${result.id}/update/files`,
                        data.upload
                    )
                    .then((res) => {
                        return {
                            annotationType: ExternalAnnotation,
                            id: res.id,
                            canvasContent: data.canvasContent,
                            fileReference: res.fileReference,
                            thumbnailReference: res.thumbnailReference,
                            parentId: fileId,
                            fileType: res.fileType,
                            filename: res.filename,
                            grandparentId: res.grandparentId
                        };
                    });
            });
    }

    static saveExternalDeliveryFileRevision(fileId: number, data: any): Promise<any> {
        return new Api()
            .put(`/views/planannotation/external/${fileId}/update`, {
                canvasContent: data.canvasContent
            })
            .then((result) => {
                return new Api()
                    .postFiles(
                        `/views/planannotation/external/${result.id}/update/files`,
                        data.upload
                    )
                    .then((res) => {
                        return {
                            annotationType: ExternalDeliveryAnnotation,
                            id: res.id,
                            canvasContent: data.canvasContent,
                            fileReference: res.fileReference,
                            thumbnailReference: res.thumbnailReference,
                            parentId: fileId,
                            fileType: res.fileType,
                            filename: res.filename
                        };
                    });
            });
    }

    static updatePlacements(
        planOrderId: number,
        files: any[],
        planOrderStoreyFileId: number
    ): Promise<any> {
        return new Api().post(`/views/planannotation/${planOrderId}/placements`, {
            files: files,
            planOrderStoreyFileId: planOrderStoreyFileId
        });
    }

    static getPlacements(planOrderId: number): Promise<any> {
        return new Api().get(`/views/planannotation/${planOrderId}/placements`);
    }
}

export default PlanAnnotationsApi;
